/**
 * APTableContainer Component
 *
 * Main container component that orchestrates all table sub-components.
 * Handles scroll, sizing, variants, sorting, and table controls integration.
 *
 * @example
 * ```tsx
 * <APTableContainer
 *   columns={columns}
 *   data={data}
 *   size="medium"
 *   variant="default"
 *   sort={{ column: 'name', direction: 'asc' }}
 *   onSortChange={handleSortChange}
 *   showControls={true}
 * />
 * ```
 */

import { forwardRef, useCallback, useMemo, useState } from 'react';

import APTableControls from '@shared/components/organisms/table-controls';

import type { IAPTableProps, IAPTableRow, IAPTableSort } from '../../types';

import APTableBody from '../body';
import APTableHeader from '../header';

import './styles.scss';

// Helper function to get nested property value
const getNestedValue = <TData,>(
  obj: TData,
  path: keyof TData | ((row: TData) => unknown)
): unknown => {
  if (typeof path === 'function') {
    return path(obj);
  }
  return obj[path];
};

// Helper function to generate row key
const getDefaultRowKey = <TData,>(row: TData, index: number): string | number => {
  if (row && typeof row === 'object' && 'id' in row) {
    return (row as { id: string | number }).id;
  }
  return index;
};

/**
 * APTableContainer - Main table container
 *
 * Orchestrates all table sub-components and handles sorting,
 * data processing, and layout management.
 */
export const APTableContainer = forwardRef<HTMLDivElement, IAPTableProps>(
  <TData,>(
    {
      columns,
      data,
      size = 'medium',
      variant = 'default',
      disabled = false,
      selection,
      sort,
      onSortChange,
      onRowClick,
      onRowDoubleClick,
      emptyState,
      loadingState,
      showControls = true,
      controlsConfig,
      sticky = true,
      maxHeight,
      overflowX = 'auto',
      caption,
      className = '',
      'data-testid': testId = 'ap-table',
      headerContent,
      footerContent,
      preserveColumnWidths = false,
      getRowKey = getDefaultRowKey,
      ...rest
    }: IAPTableProps<TData>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [internalSort, setInternalSort] = useState<IAPTableSort | null>(null);

    // Use controlled or internal sort state
    const currentSort = sort !== undefined ? sort : internalSort;
    const handleSortChange = useCallback(
      (newSort: IAPTableSort | null) => {
        if (onSortChange) {
          onSortChange(newSort);
        } else {
          setInternalSort(newSort);
        }
      },
      [onSortChange]
    );

    // Convert data to table rows
    const tableRows = useMemo<IAPTableRow<TData>[]>(() => {
      if (!data || !Array.isArray(data)) return [];
      return data.map((item, index) => ({
        id: getRowKey(item, index),
        data: item,
      }));
    }, [data, getRowKey]);

    // Sort data if needed
    const sortedRows = useMemo(() => {
      if (!currentSort) return tableRows;

      const column = columns.find((col) => col.key === currentSort.column);
      if (!column) return tableRows;

      return [...tableRows].sort((a, b) => {
        if (column.sortFn) {
          const result = column.sortFn(a.data, b.data);
          return currentSort.direction === 'desc' ? -result : result;
        }

        const aValue = getNestedValue(a.data, column.accessor);
        const bValue = getNestedValue(b.data, column.accessor);

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Compare values
        let result = 0;
        if (aValue < bValue) result = -1;
        else if (aValue > bValue) result = 1;

        return currentSort.direction === 'desc' ? -result : result;
      });
    }, [tableRows, currentSort, columns]);

    // Container classes
    const containerClasses = [
      'ap-table-container',
      `ap-table-container--${size}`,
      `ap-table-container--${variant}`,
      disabled && 'ap-table-container--disabled',
      sticky && 'ap-table-container--sticky',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Table classes
    const tableClasses = ['ap-table', preserveColumnWidths && 'ap-table--preserve-widths']
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={containerClasses} data-testid={testId} {...rest}>
        {/* Header content */}
        {headerContent && <div className="ap-table__header-content">{headerContent}</div>}

        {/* Table container with scrolling */}
        <div
          className="ap-table__scroll-container"
          style={{
            maxHeight: maxHeight,
            overflowX: overflowX,
            overflowY: maxHeight ? 'auto' : 'visible',
          }}
        >
          <table className={tableClasses} role="table">
            {/* Caption for accessibility */}
            {caption && <caption className="ap-table__caption">{caption}</caption>}

            {/* Table header */}
            <APTableHeader
              columns={columns}
              sort={currentSort || undefined}
              onSortChange={handleSortChange}
              selection={selection}
            />

            {/* Table body */}
            <APTableBody
              columns={columns}
              rows={sortedRows}
              selection={selection}
              emptyState={emptyState}
              loadingState={loadingState}
              onRowClick={onRowClick}
              onRowDoubleClick={onRowDoubleClick}
            />
          </table>
        </div>

        {/* Footer content */}
        {footerContent && <div className="ap-table__footer-content">{footerContent}</div>}

        {/* Table controls */}
        {showControls && !loadingState?.loading && sortedRows.length > 0 && (
          <APTableControls totalItems={sortedRows.length} {...controlsConfig} />
        )}
      </div>
    );
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableProps<TData> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default APTableContainer;
